import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { ArrowRight, RefreshCw, Landmark, Shield, Layers } from 'lucide-react';

const capabilities = [
  {
    icon: ArrowRight,
    title: 'Payments Infrastructure',
    description: 'SEPA, SWIFT, CHAPS, FPS, ACH — domestic and cross-border payments in 30+ currencies.'
  },
  {
    icon: RefreshCw,
    title: 'FX & Treasury',
    description: 'Spot and forward FX with real-time rates and transparent pricing.'
  },
  {
    icon: Landmark,
    title: 'Accounts & IBANs',
    description: 'Dedicated IBANs and virtual accounts for corporates and fintechs globally.'
  },
  {
    icon: Shield,
    title: 'Compliance First',
    description: 'Built-in AML, KYC and sanctions screening across multiple jurisdictions.'
  },
  {
    icon: Layers,
    title: 'Whitelabel Technology',
    description: 'Fully customisable payment solutions branded as your own, with complete API access and dedicated support.'
  }
];

export const CoreCapabilities = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section 
      id="solutions" 
      className="py-16 lg:py-20 bg-white border-t border-[#E5E7EB]"
      data-testid="core-capabilities-section"
    >
      <div className="section-container">
        {/* Section Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="mb-10"
        >
          <span className="label-text text-[#6B7280] block mb-4">
            Core Capabilities
          </span>
          <h2 
            className="heading-lg text-3xl md:text-4xl lg:text-5xl text-[#1E3A5F]"
            data-testid="core-capabilities-title"
          >
            What We Do
          </h2>
        </motion.div>

        {/* Capabilities Grid */}
        <div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 border-t border-l border-[#E5E7EB]"
          data-testid="core-capabilities-grid"
        >
          {capabilities.map((capability, index) => (
            <motion.div
              key={capability.title}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ 
                duration: 0.6, 
                delay: index * 0.1, 
                ease: [0.22, 1, 0.36, 1] 
              }}
              className="p-8 lg:p-10 border-r border-b border-[#E5E7EB] card-hover"
              data-testid={`capability-${index}`}
            >
              <capability.icon 
                className="w-6 h-6 text-[#1E3A5F] mb-6" 
                strokeWidth={1.25} 
              />
              <h3 className="text-lg font-semibold text-[#1E3A5F] mb-3">
                {capability.title}
              </h3>
              <p className="text-base text-[#6B7280] leading-relaxed">
                {capability.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
